<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import WhatsAppTabsHeader from './components/WhatsAppTabsHeader.vue';
import ProductForm from './components/ProductForm.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { useWhatsappInbox } from './composables/useWhatsappInbox';

const { t } = useI18n();
const store = useStore();
const { whatsappInboxes, selectedInboxId, hasWhatsappInbox } =
  useWhatsappInbox();

const products = useMapGetter('whatsappCatalog/getProducts');
const catalogs = useMapGetter('whatsappCatalog/getCatalogs');
const isConfigured = useMapGetter('whatsappCatalog/isCatalogConfigured');
const discoveryError = useMapGetter('whatsappCatalog/getDiscoveryError');
const uiFlags = useMapGetter('whatsappCatalog/getUIFlags');

const catalogIdInput = ref('');
const productFormRef = ref(null);
const deleteDialogRef = ref(null);
const productToDelete = ref(null);

const isFetching = computed(() => uiFlags.value.isFetching);

const loadProducts = () => {
  if (!selectedInboxId.value) return;
  store.dispatch('whatsappCatalog/get', selectedInboxId.value);
};

const linkCatalog = async catalogId => {
  const id = String(catalogId || catalogIdInput.value).trim();
  if (!id) return;
  try {
    await store.dispatch('whatsappCatalog/setCatalogId', {
      inboxId: selectedInboxId.value,
      catalogId: id,
    });
    useAlert(t('WHATSAPP_SETTINGS.CATALOG.NEEDS_CONFIG.SAVED'));
  } catch (error) {
    useAlert(
      error?.message || t('WHATSAPP_SETTINGS.CATALOG.NEEDS_CONFIG.SAVE_ERROR')
    );
  }
};

const discoverCatalogs = async () => {
  try {
    await store.dispatch('whatsappCatalog/listCatalogs', selectedInboxId.value);
    if (!catalogs.value.length) {
      useAlert(t('WHATSAPP_SETTINGS.CATALOG.NEEDS_CONFIG.NONE_FOUND'));
    }
  } catch (error) {
    useAlert(
      error?.message ||
        t('WHATSAPP_SETTINGS.CATALOG.NEEDS_CONFIG.DISCOVER_ERROR')
    );
  }
};

const onSubmitProduct = async ({ id, product }) => {
  try {
    if (id) {
      await store.dispatch('whatsappCatalog/update', {
        inboxId: selectedInboxId.value,
        id,
        product,
      });
    } else {
      await store.dispatch('whatsappCatalog/create', {
        inboxId: selectedInboxId.value,
        product,
      });
    }
    productFormRef.value?.close();
    useAlert(t('WHATSAPP_SETTINGS.CATALOG.FORM.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(t('WHATSAPP_SETTINGS.CATALOG.FORM.SAVE_ERROR'));
  }
};

const confirmDelete = product => {
  productToDelete.value = product;
  deleteDialogRef.value?.open();
};

const onDeleteProduct = async () => {
  await store.dispatch('whatsappCatalog/delete', {
    inboxId: selectedInboxId.value,
    id: productToDelete.value.id,
  });
  deleteDialogRef.value?.close();
  productToDelete.value = null;
  useAlert(t('WHATSAPP_SETTINGS.CATALOG.LIST.DELETE_SUCCESS'));
};

watch(selectedInboxId, loadProducts);
onMounted(loadProducts);
</script>

<template>
  <div class="flex flex-col w-full gap-6">
    <WhatsAppTabsHeader
      v-model:inbox-id="selectedInboxId"
      active-tab="catalog"
      :inboxes="whatsappInboxes"
    />

    <div
      v-if="!hasWhatsappInbox"
      class="flex flex-col items-center justify-center gap-2 py-16 text-center"
    >
      <p class="text-sm text-n-slate-11">
        {{ t('WHATSAPP_SETTINGS.NO_INBOX') }}
      </p>
    </div>

    <div
      v-else-if="!isConfigured"
      class="flex flex-col gap-4 p-6 rounded-xl outline-1 outline outline-n-weak"
    >
      <p class="text-sm text-n-slate-11">
        {{ t('WHATSAPP_SETTINGS.CATALOG.NEEDS_CONFIG.DESCRIPTION') }}
      </p>
      <p
        v-if="discoveryError"
        class="px-3 py-2 text-sm rounded-lg bg-n-ruby-3 text-n-ruby-11"
      >
        {{
          t('WHATSAPP_SETTINGS.CATALOG.NEEDS_CONFIG.DISCOVERY_ERROR', {
            error: discoveryError,
          })
        }}
      </p>
      <div v-if="catalogs.length" class="flex flex-col gap-2">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('WHATSAPP_SETTINGS.CATALOG.NEEDS_CONFIG.FOUND') }}
        </span>
        <div class="flex flex-wrap items-center gap-2">
          <Button
            v-for="catalog in catalogs"
            :key="catalog.id"
            variant="outline"
            size="sm"
            icon="i-lucide-link"
            :label="`${catalog.name} (${catalog.id})`"
            @click="linkCatalog(catalog.id)"
          />
        </div>
      </div>
      <div class="flex items-end gap-2">
        <Input
          v-model="catalogIdInput"
          class="flex-1"
          :label="t('WHATSAPP_SETTINGS.CATALOG.NEEDS_CONFIG.CATALOG_ID')"
          placeholder="123456789012345"
        />
        <Button
          :label="t('WHATSAPP_SETTINGS.CATALOG.NEEDS_CONFIG.SAVE')"
          @click="linkCatalog()"
        />
      </div>
      <div class="flex items-center gap-2">
        <Button
          variant="ghost"
          icon="i-lucide-search"
          :label="t('WHATSAPP_SETTINGS.CATALOG.NEEDS_CONFIG.DISCOVER')"
          @click="discoverCatalogs"
        />
      </div>
    </div>

    <template v-else>
      <div class="flex items-center justify-end">
        <Button
          icon="i-lucide-plus"
          :label="t('WHATSAPP_SETTINGS.CATALOG.LIST.CREATE')"
          @click="productFormRef?.open()"
        />
      </div>

      <div v-if="isFetching" class="py-12 text-sm text-center text-n-slate-11">
        {{ t('WHATSAPP_SETTINGS.CATALOG.LIST.LOADING') }}
      </div>

      <div
        v-else-if="!products.length"
        class="py-12 text-sm text-center text-n-slate-11"
      >
        {{ t('WHATSAPP_SETTINGS.CATALOG.LIST.EMPTY') }}
      </div>

      <table v-else class="w-full text-sm">
        <thead>
          <tr class="text-left text-n-slate-11 border-b border-n-weak">
            <th class="py-2 font-medium">
              {{ t('WHATSAPP_SETTINGS.CATALOG.LIST.NAME') }}
            </th>
            <th class="py-2 font-medium">
              {{ t('WHATSAPP_SETTINGS.CATALOG.LIST.RETAILER_ID') }}
            </th>
            <th class="py-2 font-medium">
              {{ t('WHATSAPP_SETTINGS.CATALOG.LIST.PRICE') }}
            </th>
            <th class="py-2 font-medium">
              {{ t('WHATSAPP_SETTINGS.CATALOG.LIST.AVAILABILITY') }}
            </th>
            <th class="py-2" />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="product in products"
            :key="product.id"
            class="border-b border-n-weak"
          >
            <td class="py-3 font-medium text-n-slate-12">{{ product.name }}</td>
            <td class="py-3 text-n-slate-11">{{ product.retailer_id }}</td>
            <td class="py-3 text-n-slate-11">{{ product.price }}</td>
            <td class="py-3 text-n-slate-11">{{ product.availability }}</td>
            <td class="py-3 text-right">
              <Button
                variant="ghost"
                size="sm"
                icon="i-lucide-pencil"
                @click="productFormRef?.open(product)"
              />
              <Button
                variant="ghost"
                color="ruby"
                size="sm"
                icon="i-lucide-trash-2"
                @click="confirmDelete(product)"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <ProductForm ref="productFormRef" @submit="onSubmitProduct" />

    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="t('WHATSAPP_SETTINGS.CATALOG.LIST.DELETE_TITLE')"
      :description="t('WHATSAPP_SETTINGS.CATALOG.LIST.DELETE_CONFIRM')"
      :confirm-button-label="t('WHATSAPP_SETTINGS.CATALOG.LIST.DELETE')"
      @confirm="onDeleteProduct"
    />
  </div>
</template>
